// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2022-12-08 18:06:58 +0100 (Thu, 08 Dec 2022) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 131191 $
//  Purpose   : rules for state nodes
// =============================================================================
// ---Third-party imports

// ---Framework imports
import {has, keys, flatten, sorted, get} from 'ldash'
import {Actions} from 'uix/registries'
import log from 'uix/logging'
import {translateState, isStateDomainVisible} from 'uix/states'
import {hasGroup} from 'uix/auth'

// ---Local imports
import {NTYPE} from './const'
import {ID, getExpansionState} from '../utils'

// ---Globals

export const DOMAIN_BYSTATE = ({
  domains,
  loadedDomains,
  buildStaticDomainNode,
  isVlmEnabled,
  stateConfig,
}) => ({
  buildAfter: NTYPE.DOMAIN,
  isEnabled: node => isVlmEnabled('states', node.dname, domains, loadedDomains),
  buildNodes: flatNodes =>
    flatNodes[NTYPE.DOMAIN].map(dnode =>
      buildStaticDomainNode(NTYPE.DOMAIN_BYSTATE, dnode, 'Status', has(stateConfig, dnode.dname))
    ),
  getChildren: node => Actions.loadStatesConfig(node.dname),
  storeMap: root => ({stateConfig: root.config.states}),
})

export const BYSTATE_AXIS = ({
  domains,
  i18n,
  loadedDomains,
  treeAvailableStates,
  stateConfig,
  user,
}) => ({
  buildAfter: NTYPE.DOMAIN_BYSTATE,
  isEnabled: node => !domains.data[node.dname].isGhost,
  buildNodes: flatNodes => {
    return flatten(
      flatNodes[NTYPE.DOMAIN_BYSTATE].map(node => {
        let {dname, id: parentId} = node
        let ddata = loadedDomains[dname]
        if (!ddata) {
          return []
        }
        let dcfg = ddata.dconfig

        if (!stateConfig[dname]) {
          return [] // still loading
        }

        // XXX: need to access vl.states.activeStateTypes here
        let statusTypes = keys(dcfg.states.visible).filter(k =>
          isStateDomainVisible(k, dname, dcfg, user, stateConfig[dname])
        )

        let stateTypeNodes = statusTypes.map(statusType => {
          let id = ID(parentId, statusType)
          let {loaded, children} = getExpansionState(has(treeAvailableStates[dname], statusType))
          return {
            nodeType: NTYPE.BYSTATE_AXIS,
            id,
            idContext: id,
            parentId,
            odid: node.odid,
            title: i18n.get(`${statusType}_state_tree`),
            dname,
            statusType,
            loaded,
            children,
          }
        })
        stateTypeNodes = sorted(stateTypeNodes, node => node.title)
        return stateTypeNodes
      })
    )
  },
  // trigger two actions
  getChildren: node => {
    let actions = []
    if (!treeAvailableStates[node.dname]?.[node.statusType]) {
      actions.push(Actions.getAvailableStates(node.dname, node.statusType))
    }
    //~ if (!has(stateConfig, `${node.dname}.${node.statusType}`)) {
    //~ actions.push(Actions.loadStatesConfig(node.dname, node.statusType))
    //~ }
    return actions
  },
})

export const BYSTATE_STATE = ({
  domains,
  i18n,
  loadedDomains,
  treeChildData,
  treeAvailableStates,
  stateConfig,
}) => ({
  buildAfter: NTYPE.BYSTATE_AXIS,
  isEnabled: node => !domains.data[node.dname].isGhost,
  buildNodes: flatNodes => {
    let buildNode = (
      di18n,
      odid,
      dname,
      parentId,
      statusType,
      statusName,
      statusValue,
      count,
      idOverrride
    ) => {
      let label = translateState(di18n, statusType, statusValue)
      let id = idOverrride || ID(parentId, statusName)
      let {loaded, children} = getExpansionState(Boolean(get(treeChildData, `${id}.0`)))
      return {
        nodeType: NTYPE.BYSTATE_STATE,
        id,
        idContext: id,
        parentId,
        odid,
        title: `${label || statusName}` + (count !== null ? ` (${count})` : ''),
        dname,
        statusType,
        statusName: `${statusType}:${statusName}`,
        loaded,
        children,
      }
    }

    // children for state axis nodes
    let allStateValueNodes = flatten(
      flatNodes[NTYPE.BYSTATE_AXIS].map(node => {
        let {dname, odid, statusType, id: parentId} = node
        let stateCounts = get(treeAvailableStates[dname], statusType, [])
        let ddata = loadedDomains[dname]

        if (!ddata) {
          return []
        }
        let di18n = ddata.i18n[i18n._language]

        let stateValueNodes = stateCounts.map(({name: statusName, count, value: statusValue}) =>
          buildNode(di18n, odid, dname, parentId, statusType, statusName, statusValue, count)
        )
        stateValueNodes = sorted(stateValueNodes, node => node.title)
        return stateValueNodes
      })
    )
    // children for state root node
    let customStateValueNodes = flatten(
      flatNodes[NTYPE.DOMAIN_BYSTATE].map(node => {
        let {dname, odid, id: parentId} = node
        let ddata = loadedDomains[dname]
        if (!ddata) {
          return []
        }
        let dcfg = ddata.dconfig
        let di18n = ddata.i18n[i18n._language]
        let nodes = []
        if (dcfg.vlm.tree.state_resource_incomplete) {
          nodes.push(
            buildNode(
              di18n,
              odid,
              dname,
              parentId,
              'resource',
              'incomplete',
              '1',
              null,
              ID(parentId, 'resource', 'incomplete')
            )
          )
        }
        return nodes
      })
    )
    return [...allStateValueNodes, ...customStateValueNodes]
  },
  getChildren: node =>
    Actions.getChildren({
      stateNames: node.statusName,
      typeName: 'metadata',
      odid: node.odid,
      idContext: node.idContext,
      links: false,
      subdomains: true,
      deleted: false,
    }),
})
